import * as React from "react";
import { BrancheZweiSelect } from "./BrancheZweiSelect";
import {
  findGruppe,
  useBrancheGruppen,
  type BrancheGruppe,
} from "../../../data/brancheGruppen";
import type { BrancheGruppenWahl } from "./brancheGruppenLogik";
import type { BrancheBegriffe } from "../../../utils/koerperschaftBegriff";

/**
 * Branche(n) einer Firma in der ANLAGE: die erste ist die HAUPTbranche (sie
 * steht am Kontakt als `branchengruppeId` + `brancheFreitext`), darunter
 * beliebig viele weitere.
 *
 * Warum überhaupt mehrere: Ein Unternehmen betreibt oft Handel UND Werkstatt
 * UND Vermietung, und jedes Geschäft trägt eigene Risiken.
 *
 * Der Adapter existiert, weil die Anlage ihren Zustand als Liste mit einer
 * IMMER vorhandenen (evtl. leeren) Zeile führt, das Widget aber nur belegte
 * Zeilen kennt.
 */
export interface BrancheGruppenEintrag {
  branchengruppeId: number | null;
  /** „Sonstiges"-Freitext dieser Zeile. Leerstring statt null: Der Anlage-State
   *  ist ein Formularzustand, kein Datenbankwert. */
  freitext: string;
}

export const BrancheGruppenListe: React.FC<{
  value: BrancheGruppenEintrag[];
  onChange: (next: BrancheGruppenEintrag[]) => void;
  /** Meldet zusätzlich Gruppe + Freitext der HAUPTbranche (für `industry`). */
  onHauptbrancheGruppe?: (gruppe: BrancheGruppe | null, freitext: string) => void;
  /** Beschriftungen nach Rechtsform (Verein: „Ausrichtung und Schwerpunkt"). */
  begriffe?: BrancheBegriffe;
}> = ({ value, onChange, onHauptbrancheGruppe, begriffe }) => {
  const { rows } = useBrancheGruppen();

  // Für die Auswahl zählen nur belegte Zeilen — eine leere ist keine Branche.
  const auswahl: BrancheGruppenWahl[] = React.useMemo(
    () =>
      value
        .filter((e) => e.branchengruppeId != null)
        .map((e) => ({
          branchengruppeId: e.branchengruppeId as number,
          freitext: e.freitext ?? "",
        })),
    [value],
  );

  const handleChange = (next: BrancheGruppenWahl[]) => {
    // ACHTUNG: Hier werden neue Objekte gebaut, nicht gespreadet. Jedes
    // künftige Feld muss HIER mit aufgenommen werden — sonst verschwindet es
    // zwischen Widget und Anlage-State, ohne dass etwas fehlschlägt.
    onChange(
      next.map((a) => ({
        branchengruppeId: a.branchengruppeId,
        freitext: a.freitext ?? "",
      })),
    );
    // Der Anzeigepfad der Hauptbranche wird zusätzlich als Freitext
    // gespiegelt; wechselt sie (oder fällt weg), muss der Spiegel mitziehen.
    const haupt = next[0];
    onHauptbrancheGruppe?.(
      haupt ? (findGruppe(rows, haupt.branchengruppeId) ?? null) : null,
      haupt?.freitext ?? "",
    );
  };

  return (
    <BrancheZweiSelect
      value={auswahl}
      onChange={handleChange}
      idBase="anlage-branchengruppe"
      begriffe={begriffe}
    />
  );
};

export default BrancheGruppenListe;
